/**
 * StockView — Sprint 17 S17-13 + S17-Z10.
 *
 * Vista del tab "Stock" en /tesoreria/recursos. Componente puro:
 * recibe `items`, `resumen`, `loading`, `error`, `filtroTipo` y
 * handlers. NO hace fetch. El wrapper en Recursos.tsx orquesta.
 *
 * Layout:
 * - Cards de resumen por tipo (llavero / control_remoto / pila / otro):
 *   cantidad disponible + valor de venta del próximo FIFO.
 * - Filtro por tipo.
 * - Tabla **agrupada por compra** (Sprint 17 S17-Z10): cada fila es una
 *   compra (mismo tipo + costo + valor_venta + ingresado_at) con la
 *   cantidad disponible. Solo grupos con al menos 1 unidad disponible
 *   se muestran. Click en una fila abre `<ModalDetalleCompra />` con
 *   las unidades individuales (incluyendo asignadas/consumidas para
 *   auditoría completa).
 *
 * El filtroEstado externo se mantiene para retrocompatibilidad pero la
 * vista por default agrupa todos los estados — el endpoint debe traer
 * `estado='todos'` para que el drill-down sea útil.
 */
import { useMemo, useState } from 'react';
import type {
  EstadoStock,
  StockRecursoDisponibleResumen,
  StockRecursoRead,
  TipoStock,
} from '../../lib/api/types';
import { Button } from '../ui/Button';
import { formatFecha } from '../../lib/format';
import { Monto, Num } from './Monto';
import { ModalDetalleCompra } from './ModalDetalleCompra';

interface Props {
  items: StockRecursoRead[];
  resumen: StockRecursoDisponibleResumen[];
  loading: boolean;
  error: string | null;
  filtroTipo: TipoStock | 'todos';
  filtroEstado: EstadoStock | 'todos';
  onFiltroTipoChange: (t: TipoStock | 'todos') => void;
  onFiltroEstadoChange: (e: EstadoStock | 'todos') => void;
  onIngresar: () => void;
  // S17-13b — asignación directa (vía de excepción).
  onAsignacionDirecta: () => void;
}

const ETIQUETA_TIPO: Record<TipoStock, string> = {
  llavero: 'Llaveros',
  control_remoto: 'Controles remotos',
  pila: 'Pilas',
  otro: 'Otros',
};

const TIPOS: TipoStock[] = ['llavero', 'control_remoto', 'pila', 'otro'];

// Sprint 17 S17-Z10: agrupador por compra. Una compra es la combinación
// (tipo, costo_compra, valor_venta, ingresado_at). Dos llaveros distintos
// ingresados en el mismo POST quedan en el mismo grupo.
interface GrupoCompra {
  key: string;
  tipo: TipoStock;
  costoCompra: number;
  valorVenta: number;
  ingresadoAt: string;
  items: StockRecursoRead[];
  disponibles: number;
  asignados: number;
  consumidos: number;
  dadosBaja: number;
}

function agruparCompras(items: StockRecursoRead[]): GrupoCompra[] {
  const mapa = new Map<string, GrupoCompra>();
  for (const it of items) {
    const key = `${it.tipo}|${it.costo_compra}|${it.valor_venta}|${it.ingresado_at}`;
    let g = mapa.get(key);
    if (!g) {
      g = {
        key,
        tipo: it.tipo,
        costoCompra: it.costo_compra,
        valorVenta: it.valor_venta,
        ingresadoAt: it.ingresado_at,
        items: [],
        disponibles: 0,
        asignados: 0,
        consumidos: 0,
        dadosBaja: 0,
      };
      mapa.set(key, g);
    }
    g.items.push(it);
    if (it.estado === 'en_stock') g.disponibles++;
    else if (it.estado === 'asignado') g.asignados++;
    else if (it.estado === 'consumido') g.consumidos++;
    else if (it.estado === 'dado_de_baja') g.dadosBaja++;
  }
  // Ordenar por fecha de ingreso descendente (más reciente arriba).
  return Array.from(mapa.values()).sort((a, b) =>
    b.ingresadoAt.localeCompare(a.ingresadoAt),
  );
}

export function StockView({
  items,
  resumen,
  loading,
  error,
  filtroTipo,
  filtroEstado: _filtroEstado,
  onFiltroTipoChange,
  onFiltroEstadoChange: _onFiltroEstadoChange,
  onIngresar,
  onAsignacionDirecta,
}: Props) {
  // Indexa el resumen para acceso constante por tipo.
  const resumenPorTipo = new Map<TipoStock, StockRecursoDisponibleResumen>();
  for (const r of resumen) resumenPorTipo.set(r.tipo, r);

  // Sprint 17 S17-Z10: agrupación por compra. El drill-down necesita
  // todas las unidades (incluidas asignadas/consumidas), así que el
  // wrapper trae items sin filtro de estado.
  const [grupoExpandido, setGrupoExpandido] = useState<GrupoCompra | null>(
    null,
  );

  const grupos = useMemo(() => agruparCompras(items), [items]);
  // Solo mostrar grupos con al menos una unidad disponible — la vista
  // principal es "qué tengo disponible para asignar". Las compras
  // totalmente consumidas se ven solo en reportes históricos.
  const gruposConDisponibles = grupos.filter((g) => g.disponibles > 0);

  return (
    <div className="space-y-5">
      {/* Header del tab */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-sm text-text-muted max-w-3xl">
          Stock disponible para asignar. Cada compra al proveedor se ingresa
          como un lote con su costo y valor de venta. Los llaveros vienen
          identificados unidad por unidad; los controles, pilas y otros se
          asignan al momento.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onAsignacionDirecta}>
            Asignación directa
          </Button>
          <Button variant="primary" onClick={onIngresar}>
            + Ingresar stock
          </Button>
        </div>
      </div>

      {/* Cards resumen por tipo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TIPOS.map((t) => {
          const r = resumenPorTipo.get(t);
          const cantidad = r?.cantidad_disponible ?? 0;
          const proximoValor = r?.valor_venta_proximo;
          return (
            <div
              key={t}
              className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md px-4 py-3"
              data-testid={`resumen-${t}`}
            >
              <div className="text-[10px] uppercase tracking-wide text-text-muted">
                {ETIQUETA_TIPO[t]}
              </div>
              <div className="text-2xl font-medium text-text-primary mt-1">
                <Num>{cantidad}</Num>
              </div>
              <div className="text-[11px] text-text-muted mt-0.5">
                {cantidad === 0 ? (
                  'Sin stock'
                ) : proximoValor !== null && proximoValor !== undefined ? (
                  <>
                    Próximo: <Monto value={proximoValor} />
                  </>
                ) : (
                  'Disponibles'
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtros — Sprint 17 S17-Z10: solo tipo (estado se deriva en grupo). */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <select
          value={filtroTipo}
          onChange={(e) =>
            onFiltroTipoChange(e.target.value as TipoStock | 'todos')
          }
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por tipo"
        >
          <option value="todos">Todos los tipos</option>
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {ETIQUETA_TIPO[t]}
            </option>
          ))}
        </select>
        <span className="text-xs text-text-muted">
          <Num>{gruposConDisponibles.length}</Num>{' '}
          {gruposConDisponibles.length === 1 ? 'compra activa' : 'compras activas'}
        </span>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !error && gruposConDisponibles.length === 0 && (
        <p className="text-sm text-text-muted">
          No hay compras con stock disponible. Usa "+ Ingresar stock" para
          registrar una compra.
        </p>
      )}

      {!loading && !error && gruposConDisponibles.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
              <tr>
                <th className="text-left px-2 py-2 font-medium">Tipo</th>
                <th className="text-right px-2 py-2 font-medium">
                  Disponibles
                </th>
                <th className="text-right px-2 py-2 font-medium">
                  De total compra
                </th>
                <th className="text-right px-2 py-2 font-medium">Costo</th>
                <th className="text-right px-2 py-2 font-medium">
                  Valor venta
                </th>
                <th className="text-left px-2 py-2 font-medium">Ingresado</th>
                <th className="text-right px-2 py-2 font-medium sr-only">
                  Detalle
                </th>
              </tr>
            </thead>
            <tbody>
              {gruposConDisponibles.map((g) => (
                <tr
                  key={g.key}
                  data-testid="fila-stock"
                  onClick={() => setGrupoExpandido(g)}
                  className="border-b border-white/[0.04] hover:bg-bg-card/40 cursor-pointer"
                >
                  <td className="px-2 py-2 text-text-secondary">
                    {ETIQUETA_TIPO[g.tipo]}
                  </td>
                  <td className="px-2 py-2 text-right text-state-success font-medium">
                    <Num>{g.disponibles}</Num>
                  </td>
                  <td className="px-2 py-2 text-right text-text-muted text-xs">
                    de {g.items.length}
                  </td>
                  <td className="px-2 py-2 text-right text-text-secondary">
                    <Monto value={g.costoCompra} />
                  </td>
                  <td className="px-2 py-2 text-right text-text-primary">
                    <Monto value={g.valorVenta} />
                  </td>
                  <td className="px-2 py-2 text-text-muted">
                    {formatFecha(g.ingresadoAt)}
                  </td>
                  <td className="px-2 py-2 text-right text-[11px] text-orange">
                    Ver detalle →
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {grupoExpandido && (
        <ModalDetalleCompra
          items={grupoExpandido.items}
          tipo={grupoExpandido.tipo}
          costoCompra={grupoExpandido.costoCompra}
          valorVenta={grupoExpandido.valorVenta}
          ingresadoAt={grupoExpandido.ingresadoAt}
          onClose={() => setGrupoExpandido(null)}
        />
      )}
    </div>
  );
}
